import React from 'react';
import { Logo } from './Logo';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="cursor-pointer" onClick={() => navigate('/')}>
            <Logo height={40} />
          </div>
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-400 hover:text-[#D4F7DC] transition-colors"
          >
            <ArrowLeft size={18} />
            Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-2">Terms of Service</h1>
        <p className="text-gray-400 mb-8">HiBotics AI</p>
        <p className="text-sm text-gray-500 mb-12">Last updated: 30 January 2026</p>

        <div className="prose prose-invert max-w-none">
          <p className="text-gray-300 leading-relaxed mb-8">
            These Terms of Service ("Terms") govern your use of the HiBotics AI website and services. By accessing our website or using our services, you agree to these Terms.
          </p>
          <p className="text-white font-medium mb-12">
            If you do not agree, please do not use our services.
          </p>

          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">1. About HiBotics AI</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              HiBotics AI ("we", "us", "our") provides AI-powered voice assistants and automation solutions for small businesses in the United Kingdom.
            </p>
            <ul className="text-gray-300 space-y-2 ml-4">
              <li><strong className="text-white">Website:</strong> <a href="https://hiboticsai.com" className="text-[#D4F7DC] hover:underline">https://hiboticsai.com</a></li>
              <li><strong className="text-white">Contact email:</strong> <a href="mailto:info@hiboticsai.com" className="text-[#D4F7DC] hover:underline">info@hiboticsai.com</a></li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">2. Services Provided</h2>
            <p className="text-gray-300 leading-relaxed mb-4">HiBotics AI provides:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>AI voice receptionist services</li>
              <li>Call answering and routing</li>
              <li>FAQ handling</li>
              <li>Appointment booking using live calendars</li>
              <li>AI automation and workflow setup</li>
              <li>Ongoing maintenance and optimisation</li>
            </ul>
            <p className="text-gray-400 text-sm mt-4 italic">Services may be updated, modified, or improved at any time.</p>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">3. Eligibility</h2>
            <p className="text-gray-300 leading-relaxed">
              You must be at least 18 years old and authorised to act on behalf of a business to use our services.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">4. Trial Periods</h2>
            <p className="text-gray-300 leading-relaxed mb-4">Where offered:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Trial periods are provided for evaluation purposes only.</li>
              <li>HiBotics AI reserves the right to modify or withdraw trials at any time.</li>
              <li>Usage-based charges may still apply during trial periods.</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">5. Payments & Billing</h2>
            
            <h3 className="text-lg font-medium text-white mt-6 mb-3">a) Setup Fees</h3>
            <p className="text-gray-300">One-time setup fees are non-refundable once work has commenced.</p>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">b) Monthly Fees</h3>
            <p className="text-gray-300 mb-2">Ongoing monthly fees cover:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Maintenance</li>
              <li>Support</li>
              <li>Updates and improvements</li>
            </ul>
            <p className="text-gray-400 text-sm mt-2 italic">Monthly fees are billed in advance unless otherwise agreed.</p>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">c) Usage Charges</h3>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Voice usage is billed monthly based on call minutes and platform costs.</li>
              <li>Usage rates may vary depending on provider pricing.</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">6. Late or Failed Payments</h2>
            <p className="text-gray-300 leading-relaxed mb-4">If payment fails or remains unpaid:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Services may be temporarily suspended</li>
              <li>Continued non-payment may result in termination</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">7. Client Responsibilities</h2>
            <p className="text-gray-300 leading-relaxed mb-4">You agree to:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Provide accurate and up-to-date business information</li>
              <li>Maintain booking calendars and staff availability</li>
              <li>Inform us of changes affecting bookings or call handling</li>
              <li>Use the service lawfully and ethically</li>
            </ul>
            <p className="text-gray-400 text-sm mt-4 italic">HiBotics AI is not responsible for errors caused by incorrect information supplied by the client.</p>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">8. AI Limitations</h2>
            <p className="text-gray-300 leading-relaxed mb-4">You acknowledge that:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>AI systems may occasionally misunderstand callers</li>
              <li>Bookings may require human review</li>
              <li>The service does not replace professional judgement</li>
              <li>AI does not provide medical, legal, or financial advice</li>
            </ul>
          </section>

          {/* Section 9 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">9. Medical Disclaimer</h2>
            <p className="text-gray-300 leading-relaxed mb-4">For healthcare or wellness businesses:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>The AI assistant does not provide medical advice</li>
              <li>All health-related decisions remain the responsibility of the business and client</li>
              <li>HiBotics AI is not liable for treatment outcomes</li>
            </ul>
          </section>

          {/* Section 10 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">10. Service Availability</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We aim to maintain high availability but do not guarantee uninterrupted service.
            </p>
            <p className="text-gray-300 mb-2">Downtime may occur due to:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Platform updates</li>
              <li>Telephony outages</li>
              <li>Internet disruptions</li>
              <li>Third-party provider issues</li>
            </ul>
          </section>

          {/* Section 11 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">11. Intellectual Property</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              All software, prompts, workflows, designs, and configurations remain the intellectual property of HiBotics AI.
            </p>
            <p className="text-gray-300">
              Clients are granted a non-transferable licence to use the service during active subscription only.
            </p>
          </section>

          {/* Section 12 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">12. Data Protection</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Personal data is handled in accordance with our <a href="/privacy-policy" className="text-[#D4F7DC] hover:underline">Privacy Policy</a>.
            </p>
            <p className="text-gray-300">
              By using our services, you confirm you have the necessary consent to process customer data through AI systems.
            </p>
          </section>

          {/* Section 13 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">13. Termination</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Either party may terminate services with 30 days' written notice.
            </p>
            <p className="text-gray-300 mb-2">Upon termination:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Access to systems will be removed</li>
              <li>AI agents will be deactivated</li>
              <li>Outstanding fees remain payable</li>
            </ul>
          </section>

          {/* Section 14 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">14. Business Closure</h2>
            <p className="text-gray-300 leading-relaxed mb-4">In the event that HiBotics AI ceases trading:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Clients will be given reasonable notice where possible</li>
              <li>Services will terminate on the stated closure date</li>
              <li>No charges will apply beyond that date</li>
              <li>Any unused prepaid fees will be refunded</li>
            </ul>
          </section>

          {/* Section 15 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">15. Limitation of Liability</h2>
            <p className="text-gray-300 leading-relaxed mb-4">To the fullest extent permitted by law:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>HiBotics AI shall not be liable for indirect or consequential losses</li>
              <li>Liability is limited to fees paid within the previous 3 months</li>
              <li>We are not responsible for lost revenue, missed calls, or booking errors</li>
            </ul>
          </section>

          {/* Section 16 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">16. Indemnity</h2>
            <p className="text-gray-300 leading-relaxed mb-4">You agree to indemnify HiBotics AI against claims arising from:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Misuse of the service</li>
              <li>Incorrect information supplied</li>
              <li>Breach of these Terms</li>
              <li>Customer disputes related to your business services</li>
            </ul>
          </section>

          {/* Section 17 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">17. Third-Party Services</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              Our services rely on third-party platforms (telephony, AI, calendar providers).
            </p>
            <p className="text-gray-300">
              We are not responsible for outages or pricing changes imposed by third parties.
            </p>
          </section>

          {/* Section 18 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">18. Changes to These Terms</h2>
            <p className="text-gray-300 leading-relaxed">
              We may update these Terms occasionally. Continued use of services indicates acceptance of updated terms.
            </p>
          </section>

          {/* Section 19 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">19. Governing Law</h2>
            <p className="text-gray-300 leading-relaxed">
              These Terms are governed by the laws of England and Wales.
            </p>
          </section>

          {/* Section 20 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">20. Contact Us</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              For questions regarding these Terms:
            </p>
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
              <p className="text-white font-semibold mb-2">HiBotics AI</p>
              <p className="text-gray-300">
                Email: <a href="mailto:info@hiboticsai.com" className="text-[#D4F7DC] hover:underline">info@hiboticsai.com</a>
              </p>
              <p className="text-gray-300">
                Website: <a href="https://hiboticsai.com" className="text-[#D4F7DC] hover:underline">https://hiboticsai.com</a>
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-6">
        <div className="max-w-4xl mx-auto px-6 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} HiBotics AI. All rights reserved.
        </div>
      </footer>
    </div>
  );
};
